const _ = require('lodash')
const Levenshtein = require('levenshtein')
const Popper = require('popper.js').default
const sharedUtils = require('shared/utils')
const Emitter = require('client/lib/emitter')

require('client/lib/jquery/register-plugin')

const appSupportsEvents = typeof app === 'object' && typeof app.on === 'function'

const EVENT_HANDLER_METHODS = [
  'dismiss',
  'handleClickItem',
  'handleDismiss',
  'handleKeyDown',
  'handleKeyUp'
]
const LIST_CLASSNAME = 'comment-autocomplete'
const LIST_ACTIVE_CLASSNAME = `${LIST_CLASSNAME}--active`
const ITEM_CLASSNAME = `${LIST_CLASSNAME}__item`
const ITEM_SELECTED_CLASSNAME = `${ITEM_CLASSNAME}--selected`
const ITEM_SELECTOR = `.${ITEM_CLASSNAME}`
const ITEM_SELECTED_SELECTOR = `.${ITEM_SELECTED_CLASSNAME}`

function userMatcher(input, users) {
  const re = sharedUtils.fuzzyMatcherRegexp(input)
  return _.chain(users)
  .filter(user =>
    _.some(user.names, name => re.test(name))
  )
  .orderBy(user =>
    new Levenshtein(user.names[1], input).distance
  )
  .value()
}

class CommentAutocomplete extends Emitter {
  constructor(fieldEl, users) {
    super()
    this.fieldEl = fieldEl
    this.users = users

    this.$field = $(fieldEl)
    this.$list = $('<ul/>')
    .addClass(LIST_CLASSNAME)
    .attr('data-click', 'stop-propagation')

    $(document.body).append(this.$list)
    this._popper = new Popper(
      this.$field[0],
      this.$list[0],
      {
        placement: 'bottom',
        modifiers: {
          flip: { behaviors: ['bottom', 'top'] }
        }
      }
    )

    _.bindAll(this, EVENT_HANDLER_METHODS)
    this.bindEvents()
  }

  destroy() {
    this._popper.destroy()
    this.unbindEvents()
    this.dismiss()
    this.$list.remove()
  }

  bindEvents() {
    this.$field.on('keydown', this.handleKeyDown)
    this.$field.on('keyup', this.handleKeyUp)
    this.$list.on('click', ITEM_SELECTOR, this.handleClickItem)
    $(document).on('click', this.handleDismiss)
    if (appSupportsEvents) {
      app.on('navigationend', this.dismiss)
    }
  }

  unbindEvents() {
    this.$field.off('keydown', this.handleKeyDown)
    this.$field.off('keyup', this.handleKeyUp)
    this.$list.off('click', ITEM_SELECTOR, this.handleClickItem)
    $(document).off('click', this.handleDismiss)
    if (appSupportsEvents) {
      app.off('navigationend', this.dismiss)
    }
  }

  dismiss() {
    this.$list.removeClass(LIST_ACTIVE_CLASSNAME).empty()
  }

  handleKeyDown(event) {
    const { value, selectionStart, selectionEnd } = this.fieldEl
    if (!value) {
      return this.dismiss(event)
    }
    if (selectionStart !== selectionEnd) {
      return
    }
    switch (event.keyCode) {
      case 38: // Up Arrow
        this.handleSelectPrevious(event)
        break
      case 40: // Down Arrow
        this.handleSelectNext(event)
        break
      case 13: // Enter
      case 9: // Tab
        this.handleConfirmSelection(event)
        break
      case 27: // Escape
        this.handleDismiss(event)
        break
    }
  }

  handleKeyUp(event) {
    switch (event.keyCode) {
      case 27: // Escape
      case 38: // Up Arrow
      case 40: // Down Arrow
        return
    }
    const { value, selectionStart } = this.fieldEl
    const left = value.slice(0, selectionStart)
    const right = value.slice(selectionStart, value.length)
    const usernameMatch = left.match(/@(\w+)$/)
    const restMatch = right.match(/^(\w+)/)
    if (!usernameMatch) {
      return this.dismiss()
    }
    let username = usernameMatch[1]
    if (restMatch) {
      username += restMatch[1]
    }
    const userMatches = userMatcher(username, this.users)
    if (!userMatches.length) {
      return this.dismiss()
    }
    this.$list.addClass(LIST_ACTIVE_CLASSNAME).empty()
    .css('width', this.$field.css('width'))
    .append(userMatches.slice(0, 10).map(user => {
      const [displayName, atUserName] = user.names
      return $('<li/>')
      .addClass(ITEM_CLASSNAME)
      .attr('data-complete', atUserName)
      .html(`${displayName} <em>${atUserName}</em>`)
    }))
    this._popper.scheduleUpdate()
  }

  handleClickItem(event) {
    event.preventDefault()
    this.$field.focus()
    $(event.currentTarget).addClass(ITEM_SELECTED_CLASSNAME)
    this.handleConfirmSelection(event)
  }

  handleDismiss(event) {
    if (event.isImmediatePropagationStopped()) {
      return
    }
    if (this.$list.find(ITEM_SELECTOR).length) {
      event.stopImmediatePropagation()
      event.stopPropagation()
      this.dismiss()
    }
  }

  handleSelectPrevious(event) {
    if (this.fieldEl.value) {
      event.stopPropagation()
    }
    if (this.$list.find(ITEM_SELECTOR).length) {
      event.preventDefault()
    }
    const $active = this.$list.find(ITEM_SELECTED_SELECTOR)
    const $prev = $active.prev()
    if ($prev.length) {
      $active.removeClass(ITEM_SELECTED_CLASSNAME)
      $prev.addClass(ITEM_SELECTED_CLASSNAME)
    }
  }

  handleSelectNext(event) {
    if (this.fieldEl.value) {
      event.stopPropagation()
    }
    const $active = this.$list.find(ITEM_SELECTED_SELECTOR)
    const $next = $active.length
      ? $active.next()
      : this.$list.find(ITEM_SELECTOR).first()
    if ($next.length) {
      event.preventDefault()
      $active.removeClass(ITEM_SELECTED_CLASSNAME)
      $next.addClass(ITEM_SELECTED_CLASSNAME)
    }
  }

  handleConfirmSelection(event) {
    const $active = this.$list.find(ITEM_SELECTED_SELECTOR)
    if (!$active.length) {
      return
    }
    event.preventDefault()
    const { value, selectionStart } = this.fieldEl
    const left = value.slice(0, selectionStart)
    const right = value.slice(selectionStart, value.length)
    const usernameMatch = left.match(/@(\w+)$/)
    const username = usernameMatch[0]
    const restMatch = right.match(/^(\w+)/)
    const rest = restMatch ? restMatch[0] : ''
    const complete = $active.attr('data-complete')
    const first = value.slice(0, left.length - username.length)
    const last = value.slice(
      (first + username + rest).length,
      value.length + username.length
    )
    const pos = (first + complete + rest).length + 1
    // Update the field
    this.fieldEl.value = [first, complete, ' ', last].join('')
    this.fieldEl.setSelectionRange(pos, pos)
    this.dismiss()
  }
}

$.registerPlugin(CommentAutocomplete, 'CommentAutocomplete')

module.exports = CommentAutocomplete
